import type { DataTypeLocator } from '@helo/cms'
import { flow } from '@helo/utils'
import type { ComponentType, ReactNode } from 'react'

import { Options } from '../../Options'
import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import type {
  ChartDataOutput,
  ChartDataPoint,
  ChartDataSlice,
} from '../ChartData'
import type { ChartDataOptions } from '../ChartData/ChartData.options'
import {
  type ChartSeriesOptions,
  type ChartVisualization,
  chartSeriesOptions,
} from './ChartSeries.options'
import {
  ChartSeriesInputParts,
  ChartSeriesOutputParts,
} from './ChartSeries.parts'

export type { ChartVisualization } from './ChartSeries.options'
export { chartSeriesOptions } from './ChartSeries.options'
export type { ChartSeriesItem, ChartSeriesOptions } from './ChartSeries.options'

export type ChartSeriesInput = Omit<ChartSeriesOptions, '_series'> &
  ChartDataOptions & {
    _series?: Options.State[]
  }

export type ResolvedChartSeries = {
  measureLocator: DataTypeLocator
  visualization: ChartVisualization
  /** Stable identity for color assignment — survives series reordering. */
  seriesKey: string
  dataKey: string
  label?: string
  /** Normalized points after nullValueBehavior. */
  points: ChartDataPoint[]
  /** Display formatting from measure field + series state (CMS Format.numberParts). */
  formatDisplayValue: (value: number) => string
}

export type ChartSeriesMark = {
  color: string
  fillColor: string
  highlightColor: string
}

/** Pipeline output — resolved series array. Each item carries its own mark. */
export type ChartSeriesOutput = {
  series: ResolvedChartSeries[]
}

export interface ChartSeriesVisualizationProps {
  series: ResolvedChartSeries
  data: ChartDataSlice
}

export interface ChartSeriesRendererProps {
  renderConfig: ChartDataOutput & ChartSeriesOutput
  visualizations: Partial<
    Record<ChartVisualization, ComponentType<ChartSeriesVisualizationProps>>
  >
}

const SeriesRenderer = ({
  renderConfig,
  visualizations,
}: ChartSeriesRendererProps): ReactNode => {
  const { data, series } = renderConfig
  if (series.length === 0) return null

  return (
    <>
      {series.map((seriesItem, index) => {
        const Visualization = visualizations[seriesItem.visualization]
        if (Visualization == null) return null

        return (
          <Visualization
            key={`${seriesItem.seriesKey}-${index}`}
            series={seriesItem}
            data={data}
          />
        )
      })}
    </>
  )
}

export type ChartSeriesPriorOutput = ChartDataOutput & QueryOutput

const chartSeriesSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartSeriesInput,
    ChartSeriesOutput,
    ChartSeriesPriorOutput
  >(
    flow(
      ChartSeriesInputParts.setupInput(),
      ChartSeriesInputParts.setup_seriesInput(),
      ChartSeriesOutputParts.setSeries(),
    ),
  )

export const ChartSeries = {
  options: chartSeriesOptions,
  setup: chartSeriesSetup,
  SeriesRenderer,
}
